// What a provider says a day cost us — the FIRST of the two price arms.
//
// A usage fact states units, not money, so it is priced in this order: a
// provider cost fact for that (provider, sku, unit, day) if one exists, then
// our own rate card, then nothing at all. The card beside this one is the
// SECOND arm. Putting them on one screen is the whole point: a reader looking
// at a figure needs to know whether the provider told us or we guessed from our
// own list, and two screens would make that a thing to remember.
//
// Read-only. These rows arrive through the ingest path — a feed writes them,
// and a person writes one the same way, with `source: "person"` — so there is
// no CRUD form here and no table to keep in step with one.
//
// BOTH ARMS READ THE SAME KEY: provider, operation, model, unit, day. That is
// the key `usage_facts` and `provider_rates` already share, which is why a cost
// fact carries an operation and a model rather than a SKU that could join to
// nothing. The columns here are that key, so a reader can put a row on this
// card beside a row on the card below it and see they are about one thing.
//
// `amount_minor` is SIGNED, so a credit is a negative row. Rendered as the
// negative it is rather than hidden as a zero: a provider credit is money back,
// and a screen that showed 0.00 would say we spent nothing.

import type { ReactNode } from "react";
import styles from "./RatesPage.module.css";
import { Badge } from "../../design/Badge.js";
import { Card, CardHeader } from "../../design/Card.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { EmptyRow, Table, Td, Th } from "../../design/Table.js";
import { useProviderCostFacts } from "../../api/queries.js";
import { formatInstantDate } from "../../format/calendarDate.js";
import { formatCount, formatMoney } from "../../format/money.js";
import { factSourceLabel } from "../../format/labels.js";

export function ProviderCostFactsCard(): ReactNode {
  const facts = useProviderCostFacts();

  return (
    <Card>
      <CardHeader
        title="Provider cost facts"
        description="What a provider says a day cost us. A fact here outranks the rate card below: the card is what we would have charged, and this is what we were charged."
      />
      <QueryBoundary query={facts} label="the provider cost facts">
        {(page) => (
          <Table label="Provider cost facts">
            <thead>
              <tr>
                <Th>Day</Th>
                <Th>Provider</Th>
                <Th>Operation</Th>
                <Th>Model</Th>
                <Th>Deployment</Th>
                <Th numeric>Units</Th>
                <Th numeric>Amount</Th>
                <Th>Who said so</Th>
              </tr>
            </thead>
            <tbody>
              {page.items.length === 0 ? (
                <EmptyRow
                  colSpan={8}
                  title="No provider cost facts"
                  description="No provider cost feed has delivered a day's cost yet, so every usage fact is priced from the rate card below."
                />
              ) : (
                page.items.map((fact) => (
                  <tr key={fact.id}>
                    <Td mono nowrap>
                      {formatInstantDate(fact.day)}
                    </Td>
                    <Td strong>{fact.provider}</Td>
                    <Td mono>{fact.operation}</Td>
                    <Td mono>{fact.model ?? <span className={styles.muted}>no model</span>}</Td>
                    <Td mono nowrap>
                      {fact.deployment_slug ?? <span className={styles.muted}>every</span>}
                    </Td>
                    <Td numeric mono muted>
                      {fact.units === null ? "—" : `${formatCount(fact.units)} ${fact.unit}`}
                    </Td>
                    <Td numeric mono strong>
                      {formatMoney(fact.amount_minor, fact.currency)}
                    </Td>
                    <Td>
                      <Badge shape="tag" tone={fact.source === "person" ? "brand" : "slate"}>
                        {factSourceLabel(fact.source)}
                      </Badge>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </QueryBoundary>
    </Card>
  );
}
